import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
    inject
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService } from '@services/api-client';
import { MeasurementComparisonResponse, SavedFilterResponse } from '@services/api-client/responses';
import { convertFilterToPredicates } from 'app/features/shared/component/filter/filter-utils';
import { deserializeDateRange } from 'app/features/shared/component/filter/filter.component';
import { SelectedFilter } from 'app/features/shared/component/filter/filter-model';
import { MeasurementListFilterService } from 'app/features/measurement/services/measurement-list-filter.service';
import { buildSystemFilters, CURRENT_LIST_FILTER_ID, CURRENT_YEAR_FILTER_ID } from '../system-filters';

@UntilDestroy()
@Component({
    selector: 'app-scorecard-comparison',
    templateUrl: './scorecard-comparison.component.html',
    styleUrls: ['./scorecard-comparison.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ScorecardComparisonComponent implements OnInit {
    private readonly apiClientService = inject(ApiClientService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly translateService = inject(TranslateService);
    private readonly measurementListFilterService = inject(MeasurementListFilterService, { optional: true });

    private savedApiFilters: SavedFilterResponse[] = [];
    private currentListFilterEntry: SavedFilterResponse | null = null;
    private hasAutoSelected = false;

    @Input() public useListFilter = false;
    @Input() public allowCompare = true;
    @Input() public externalLeft: { data: MeasurementComparisonResponse; label: string; color: string } | null = null;
    @Input() public externalRight: { data: MeasurementComparisonResponse; label: string; color: string } | null = null;
    @Output() public readonly leftDataChange = new EventEmitter<MeasurementComparisonResponse | null>();

    public availableFilters: SavedFilterResponse[] = [];

    public leftFilterId: string | null = null;
    public rightFilterId: string | null = null;

    public leftData: MeasurementComparisonResponse | null = null;
    public rightData: MeasurementComparisonResponse | null = null;

    public leftLoading = false;
    public rightLoading = false;

    public showRightCard = false;

    public ngOnInit(): void {
        this.apiClientService
            .getSavedFilters$()
            .pipe(untilDestroyed(this))
            .subscribe((filters) => {
                this.savedApiFilters = filters;
                this.rebuildAvailableFilters();
                if (!this.useListFilter && !this.hasAutoSelected) {
                    this.hasAutoSelected = true;
                    setTimeout(() => {
                        this.leftFilterId = CURRENT_YEAR_FILTER_ID;
                        this.loadData('left', CURRENT_YEAR_FILTER_ID);
                        this.changeDetectorRef.markForCheck();
                    });
                }
                this.changeDetectorRef.markForCheck();
            });

        if (this.useListFilter && this.measurementListFilterService) {
            this.measurementListFilterService.filter$.pipe(untilDestroyed(this)).subscribe((filter) => {
                if (filter) {
                    if (this.currentListFilterEntry) {
                        // Update filter data in place — avoids rebuilding availableFilters and re-rendering options
                        this.currentListFilterEntry.filter = JSON.stringify(filter);
                    } else {
                        this.currentListFilterEntry = {
                            id: CURRENT_LIST_FILTER_ID,
                            name: this.translateService.instant(
                                'FEATURES.SCORECARD.SYSTEM_FILTERS.CURRENT_LIST_FILTER'
                            ) as string,
                            filter: JSON.stringify(filter)
                        };
                        this.rebuildAvailableFilters();
                    }

                    if (!this.hasAutoSelected && this.leftFilterId === null) {
                        this.hasAutoSelected = true;
                        setTimeout(() => {
                            this.leftFilterId = CURRENT_LIST_FILTER_ID;
                            this.loadData('left', CURRENT_LIST_FILTER_ID);
                            this.changeDetectorRef.markForCheck();
                        });
                    } else {
                        if (this.leftFilterId === CURRENT_LIST_FILTER_ID) {
                            this.loadData('left', CURRENT_LIST_FILTER_ID);
                        }
                        if (this.rightFilterId === CURRENT_LIST_FILTER_ID) {
                            this.loadData('right', CURRENT_LIST_FILTER_ID);
                        }
                    }
                } else {
                    this.currentListFilterEntry = null;
                    this.rebuildAvailableFilters();
                }
                this.changeDetectorRef.markForCheck();
            });
        }
    }

    private rebuildAvailableFilters(): void {
        const systemFilters = buildSystemFilters({
            currentMonth: this.translateService.instant('FEATURES.SCORECARD.SYSTEM_FILTERS.CURRENT_MONTH') as string,
            previousMonth: this.translateService.instant('FEATURES.SCORECARD.SYSTEM_FILTERS.PREVIOUS_MONTH') as string,
            currentYear: this.translateService.instant('FEATURES.SCORECARD.SYSTEM_FILTERS.CURRENT_YEAR') as string
        });
        const currentFilterEntries = this.currentListFilterEntry ? [this.currentListFilterEntry] : [];
        this.availableFilters = [...currentFilterEntries, ...systemFilters, ...this.savedApiFilters];
    }

    public toggleCompare(): void {
        this.showRightCard = !this.showRightCard;
        if (!this.showRightCard) {
            this.rightFilterId = null;
            this.rightData = null;
        }
        this.changeDetectorRef.markForCheck();
    }

    public swapFilters(): void {
        [this.leftFilterId, this.rightFilterId] = [this.rightFilterId, this.leftFilterId];
        [this.leftData, this.rightData] = [this.rightData, this.leftData];
        this.changeDetectorRef.markForCheck();
    }

    public onLeftFilterSelected(filterId: string | null): void {
        this.leftFilterId = filterId;
        if (filterId) {
            this.loadData('left', filterId);
        } else {
            this.leftData = null;
            this.leftDataChange.emit(null);
        }
        this.changeDetectorRef.markForCheck();
    }

    public onRightFilterSelected(filterId: string | null): void {
        this.rightFilterId = filterId;
        if (filterId) {
            this.loadData('right', filterId);
        } else {
            this.rightData = null;
        }
        this.changeDetectorRef.markForCheck();
    }

    public get canSwap(): boolean {
        return !!(this.leftFilterId && this.rightFilterId);
    }

    private loadData(side: 'left' | 'right', filterId: string): void {
        const filter = this.availableFilters.find((f) => f.id === filterId);
        if (!filter) return;

        const selectedFilter = deserializeDateRange(JSON.parse(filter.filter) as SelectedFilter);
        const predicates = convertFilterToPredicates(selectedFilter);

        if (side === 'left') {
            this.leftLoading = true;
        } else {
            this.rightLoading = true;
        }
        this.changeDetectorRef.markForCheck();

        this.apiClientService
            .getMeasurementComparisonByFilters$(predicates)
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (data) => {
                    if (side === 'left') {
                        this.leftData = data;
                        this.leftDataChange.emit(data);
                        this.leftLoading = false;
                    } else {
                        this.rightData = data;
                        this.rightLoading = false;
                    }
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    if (side === 'left') {
                        this.leftLoading = false;
                    } else {
                        this.rightLoading = false;
                    }
                    this.changeDetectorRef.markForCheck();
                }
            });
    }
}
